/**
 * xr_ui_layout — the VR UI's page models and geometry (XR P2 "VR UI / UX", 2026-09-28). PURE: no three, no DOM, no
 * rows — the modules in `modules/vr/` draw what this lays out, and `verify:xr-ui` pins it.
 *
 * WHAT IS LEGIBLE IN A HEADSET (the research this sizes from — sources in the hand-back):
 *  · Meta typography: ≥ 14 px for minimal legibility, ≥ 18 px for comfortable reading. Android XR: ≥ 14 dp, and
 *    1 dp ≈ 0.868 dmm (a distance-independent millimetre = 1 mm seen at 1 m). 18 dp ≈ 15.6 dmm ≈ 0.9° of view, so the
 *    body text here is ≥ 1.0° and the smallest text ≥ 0.75°.
 *  · Meta hands UI: targets ≥ 2.5–3° (≈ 22 mm at touch distance), 12 mm between adjacent targets, indirect (ray) UI
 *    no nearer than 0.8 m, direct touch at 0.42–0.46 m. Android XR: primary content inside ~41° of view.
 *  · Text panels 1.2–1.5 m away, world-anchored, never head-locked; the panel sits a little below the eye line and is
 *    angled to face the eye.
 * `legibility()` computes the angles from these constants so the harness can hold the line.
 *
 * TWO SURFACES: the WORLD PANEL (1.0 × 0.6875 m, 1.35 m away — every page) and the WRIST MENU (0.20 × 0.16 m, read at
 * ~0.42 m — the launcher + the combat HUD). Both are ONE canvas-textured quad each (one draw call), redrawn only when
 * their content signature changes.
 */

import { COMFORT_DEFAULTS, SNAP_CHOICES, type XRComfort } from './xr_comfort.js';
import type { XRPerfSnapshot } from './xr_perf.js';

// ─── Geometry ───────────────────────────────────────────────────────────────────

export interface Rect { x: number; y: number; w: number; h: number }

/** The world panel: physical size, canvas size, reading distance. */
export const PANEL = { wM: 1.0, hM: 0.6875, cw: 1024, ch: 704, distanceM: 1.35, dropM: 0.12 } as const;
/** The wrist menu (read at arm's length, ~ direct-touch distance). */
export const WRIST = { wM: 0.2, hM: 0.16, cw: 640, ch: 512, viewM: 0.42 } as const;
/** The summon orb (for sessions with no X button: hands, Vision Pro's transient pointer). */
export const ORB = { radiusM: 0.035, distanceM: 0.85, dropM: 0.45, followDeg: 40, rate: 4 } as const;

/** Font sizes (canvas px) per surface. */
export const FONT = { title: 30, body: 26, small: 20, big: 34, nav: 24 } as const;
export const WRIST_FONT = { label: 22, hud: 26, small: 20 } as const;

/** The em angle of `px` canvas pixels on a surface of `pxPerM` seen from `distanceM`, in degrees. */
export function pxToDeg(px: number, pxPerM: number, distanceM: number): number {
  return (2 * Math.atan((px / pxPerM) / 2 / distanceM) * 180) / Math.PI;
}

/** Every size that matters, as an angle — what `verify:xr-ui` holds against the research minimums. */
export function legibility(): {
  panel: { bodyDeg: number; smallDeg: number; rowDeg: number; navDeg: number; widthDeg: number; heightDeg: number; distanceM: number };
  wrist: { labelDeg: number; hudDeg: number; buttonDeg: number; buttonMm: number; gapMm: number; distanceM: number };
} {
  const pp = PANEL.cw / PANEL.wM;
  const wp = WRIST.cw / WRIST.wM;
  const L = WRIST_LAYOUT;
  return {
    panel: {
      bodyDeg: pxToDeg(FONT.body, pp, PANEL.distanceM),
      smallDeg: pxToDeg(FONT.small, pp, PANEL.distanceM),
      rowDeg: pxToDeg(ROW_H, pp, PANEL.distanceM),
      navDeg: pxToDeg(LAYOUT.nav.rowH, pp, PANEL.distanceM),
      widthDeg: pxToDeg(PANEL.cw, pp, PANEL.distanceM),
      heightDeg: pxToDeg(PANEL.ch, pp, PANEL.distanceM),
      distanceM: PANEL.distanceM,
    },
    wrist: {
      labelDeg: pxToDeg(WRIST_FONT.label, wp, WRIST.viewM),
      hudDeg: pxToDeg(WRIST_FONT.hud, wp, WRIST.viewM),
      buttonDeg: pxToDeg(L.button, wp, WRIST.viewM),
      buttonMm: (L.button / wp) * 1000,
      gapMm: (L.gap / wp) * 1000,
      distanceM: WRIST.viewM,
    },
  };
}

/** The world panel's frame: header, the page rail on the left, the content area, the status footer. */
export const LAYOUT = {
  header: { x: 0, y: 0, w: 1024, h: 64 },
  close: { x: 952, y: 4, w: 64, h: 56 },
  nav: { x: 12, y: 72, w: 228, rowH: 64 },
  content: { x: 256, y: 72, w: 752, h: 576 },
  footer: { x: 0, y: 656, w: 1024, h: 48 },
} as const;
/** A list row's height on the world panel (≥ 2.5° at 1.35 m). */
export const ROW_H = 64;

export function inside(r: Rect, px: number, py: number): boolean {
  return px >= r.x && px < r.x + r.w && py >= r.y && py < r.y + r.h;
}

// ─── Widgets + hit testing ──────────────────────────────────────────────────────

/** A clickable region of a surface. `id` is `<kind>:<arg>` — the surface's click handler parses it. */
export interface Widget { id: string; rect: Rect }

/** The widget under canvas pixel (px, py) — the LAST one wins (drawn on top), none → null. */
export function hitWidget(widgets: readonly Widget[], px: number, py: number): Widget | null {
  for (let i = widgets.length - 1; i >= 0; i--) if (inside(widgets[i].rect, px, py)) return widgets[i];
  return null;
}

/** A surface uv (three's PlaneGeometry: u → right, v → UP) to canvas pixels (y DOWN). */
export function uvToCanvas(u: number, v: number, cw: number, ch: number): { px: number; py: number } {
  return { px: u * cw, py: (1 - v) * ch };
}

// ─── Pages ──────────────────────────────────────────────────────────────────────

export type PageId = 'main' | 'worlds' | 'hud' | 'comfort' | 'perf' | 'chat' | 'inventory' | 'map' | 'dialogue';
export const PAGES: ReadonlyArray<{ id: PageId; label: string; icon: string }> = [
  { id: 'main', label: 'Main', icon: 'home' },
  { id: 'worlds', label: 'My Worlds', icon: 'globe' },
  { id: 'hud', label: 'HUD', icon: 'star' },
  { id: 'comfort', label: 'Comfort', icon: 'comfort' },
  { id: 'perf', label: 'Perf', icon: 'chart' },
  { id: 'chat', label: 'Chat', icon: 'chat' },
  { id: 'inventory', label: 'Inventory', icon: 'box' },
  { id: 'map', label: 'Map', icon: 'map' },
  { id: 'dialogue', label: 'Dialogue', icon: 'talk' },
];

export function isPageId(v: unknown): v is PageId { return PAGES.some((p) => p.id === v); }

/** The page rail's rows (one per page) + the header's close button. */
export function frameWidgets(): Widget[] {
  const out: Widget[] = PAGES.map((p, i) => ({
    id: `page:${p.id}`,
    rect: { x: LAYOUT.nav.x, y: LAYOUT.nav.y + i * LAYOUT.nav.rowH, w: LAYOUT.nav.w, h: LAYOUT.nav.rowH - 6 },
  }));
  out.push({ id: 'close:panel', rect: { ...LAYOUT.close } });
  return out;
}

/** Stacked rows down the content area (the Main / Worlds pages). */
export function rowRects(n: number, top = LAYOUT.content.y + 8, rowH = ROW_H): Rect[] {
  const out: Rect[] = [];
  for (let i = 0; i < n; i++) out.push({ x: LAYOUT.content.x, y: top + i * (rowH + 8), w: LAYOUT.content.w, h: rowH });
  return out;
}

/**
 * A paged list: `rowsPerPage` rows of `rowH` from `top`, a pager row at the bottom of the content area when there is
 * more than one page. `page` is clamped. Items on this page = [first, first + rows.length).
 */
export function listLayout(total: number, page: number, rowsPerPage: number, top: number, rowH: number): {
  page: number; pages: number; first: number; rows: Rect[]; prev: Rect | null; next: Rect | null;
} {
  const per = Math.max(1, Math.floor(rowsPerPage));
  const pages = Math.max(1, Math.ceil(total / per));
  const p = Math.max(0, Math.min(pages - 1, Math.floor(page)));
  const first = p * per;
  const n = Math.max(0, Math.min(per, total - first));
  const rows: Rect[] = [];
  for (let i = 0; i < n; i++) rows.push({ x: LAYOUT.content.x, y: top + i * (rowH + 6), w: LAYOUT.content.w, h: rowH });
  const pagerY = LAYOUT.content.y + LAYOUT.content.h - 60;
  const prev = pages > 1 ? { x: LAYOUT.content.x + LAYOUT.content.w - 340, y: pagerY, w: 160, h: 56 } : null;
  const next = pages > 1 ? { x: LAYOUT.content.x + LAYOUT.content.w - 170, y: pagerY, w: 160, h: 56 } : null;
  return { page: p, pages, first, rows, prev, next };
}

/** Greedy word wrap to `maxW` with an injected measurer; a word wider than a line is hard-broken; ≤ `maxLines`
 *  lines, the last ending in '…' when text was cut. */
export function wrapText(text: string, maxW: number, measure: (s: string) => number, maxLines = 99): string[] {
  const words = String(text).replace(/\s+/g, ' ').trim().split(' ').filter((w) => w.length > 0);
  const lines: string[] = [];
  let cur = '';
  const push = (s: string): boolean => { lines.push(s); return lines.length >= maxLines; };
  let cut = false;
  outer: for (let wi = 0; wi < words.length; wi++) {
    let w = words[wi];
    // Hard-break a word that cannot fit a line on its own.
    while (measure(w) > maxW && w.length > 1) {
      if (cur) { if (push(cur)) { cut = true; break outer; } cur = ''; }
      let k = w.length - 1;
      while (k > 1 && measure(w.slice(0, k)) > maxW) k--;
      if (push(w.slice(0, k))) { cut = wi < words.length - 1 || k < w.length; break outer; }
      w = w.slice(k);
    }
    const cand = cur ? `${cur} ${w}` : w;
    if (measure(cand) <= maxW) { cur = cand; continue; }
    if (push(cur)) { cut = true; break; }
    cur = w;
  }
  if (!cut && cur) lines.push(cur);
  else if (cut && lines.length > 0) {
    let last = lines[lines.length - 1];
    while (last.length > 1 && measure(`${last}…`) > maxW) last = last.slice(0, -1);
    lines[lines.length - 1] = `${last}…`;
  }
  return lines.slice(0, maxLines);
}

// ─── The Comfort page (writes lib/xr_comfort through setComfort) ────────────────

export type ComfortTileId =
  | 'move' | 'turn' | 'snapDeg' | 'smoothDegPerS' | 'vignette' | 'seated' | 'seatedOffsetM' | 'heightOffsetM'
  | 'moveHand' | 'vehicleComfort' | 'reset'
  // space campaign L (2026-10-01): the SECTOR's tiles (the page shows them while you fly — the sticks fly a ship there)
  | 'spaceBank' | 'spaceRoll' | 'spaceRollRate' | 'spaceCue' | 'spaceFrame' | 'spaceHudScale' | 'spaceEffects';
export interface ComfortTile { id: ComfortTileId; label: string; value: string; kind: 'cycle' | 'step' | 'action'; dim: boolean }

export const SMOOTH_CHOICES = [60, 90, 120, 180] as const;
export const VIGNETTE_CHOICES: ReadonlyArray<{ v: number; label: string }> = [
  { v: 0, label: 'Off' }, { v: 0.3, label: 'Light' }, { v: 0.6, label: 'Medium' }, { v: 0.9, label: 'Strong' },
];
export const SEATED_OFFSET_CHOICES = [0.25, 0.35, 0.45, 0.55, 0.65, 0.75] as const;
export const HEIGHT_STEP_M = 0.05;

/** The index of the choice nearest `v`. */
export function nearestIndex(choices: readonly number[], v: number): number {
  let best = 0;
  for (let i = 1; i < choices.length; i++) if (Math.abs(choices[i] - v) < Math.abs(choices[best] - v)) best = i;
  return best;
}
/** The choice after the one nearest `v`, wrapping. */
export function nextChoice(choices: readonly number[], v: number): number {
  return choices[(nearestIndex(choices, v) + 1) % choices.length];
}

const cm = (m: number): string => `${m >= 0 ? '+' : '−'}${Math.round(Math.abs(m) * 100)} cm`;

/** The tiles the Comfort page shows, from the live comfort model. `dim` = the setting does nothing in the current mode. */
export function comfortTiles(c: XRComfort): ComfortTile[] {
  const vig = VIGNETTE_CHOICES[nearestIndex(VIGNETTE_CHOICES.map((x) => x.v), c.vignette)].label;
  return [
    { id: 'move', label: 'Movement', value: c.move === 'slide' ? 'Slide (stick)' : 'Teleport', kind: 'cycle', dim: false },
    { id: 'turn', label: 'Turning', value: c.turn === 'snap' ? 'Snap' : 'Smooth', kind: 'cycle', dim: false },
    { id: 'snapDeg', label: 'Snap angle', value: `${Math.round(c.snapDeg)}°`, kind: 'cycle', dim: c.turn !== 'snap' },
    { id: 'smoothDegPerS', label: 'Smooth turn speed', value: `${Math.round(c.smoothDegPerS)}°/s`, kind: 'cycle', dim: c.turn !== 'smooth' },
    { id: 'vignette', label: 'Comfort vignette', value: vig, kind: 'cycle', dim: false },
    { id: 'seated', label: 'Seated play', value: c.seated ? 'On' : 'Off', kind: 'cycle', dim: false },
    { id: 'seatedOffsetM', label: 'Seated lift', value: `${Math.round(c.seatedOffsetM * 100)} cm`, kind: 'cycle', dim: !c.seated },
    { id: 'heightOffsetM', label: 'Height nudge', value: cm(c.heightOffsetM), kind: 'step', dim: false },
    { id: 'moveHand', label: 'Move hand', value: c.moveHand === 'left' ? 'Left (right turns)' : 'Right (left turns)', kind: 'cycle', dim: false },
    { id: 'vehicleComfort', label: 'Vehicle comfort', value: c.vehicleComfort ? 'On' : 'Off', kind: 'cycle', dim: false },
    { id: 'reset', label: 'Defaults', value: 'Reset all', kind: 'action', dim: false },
  ];
}

// ── The sector's Comfort tiles (space campaign L) ─────────────────────────────────────────────────────────────────
export const SPACE_ROLL_RATE_CHOICES = [0.35, 0.6, 1] as const;
export const SPACE_HUD_SCALE_CHOICES = [0.85, 1, 1.15, 1.3, 1.5] as const;
const pct = (v: number): string => `${Math.round(v * 100)}%`;

/** The SECTOR's comfort tiles (12 — the grid's capacity): what a headset banks, roll comfort, the vignette and its cue,
 *  the cockpit frame, the HUD's size, the screen effects, and the shared move hand / seated / height / reset. */
export function sectorComfortTiles(c: XRComfort): ComfortTile[] {
  const vig = VIGNETTE_CHOICES[nearestIndex(VIGNETTE_CHOICES.map((x) => x.v), c.vignette)].label;
  const bank = c.spaceBank === 'full' ? 'Full (like the desktop)' : c.spaceBank === 'reduced' ? 'Reduced' : 'Off (the horizon stays)';
  const roll = c.spaceRoll === 'lock' ? 'Horizon lock' : c.spaceRoll === 'assist' ? 'Auto-level' : 'Free';
  const cue = c.spaceCue === 'off' ? 'Never' : c.spaceCue === 'turns' ? 'Turns' : 'Turns + thrust';
  return [
    { id: 'spaceBank', label: 'Bank in turns', value: bank, kind: 'cycle', dim: false },
    { id: 'spaceRoll', label: 'Roll comfort', value: roll, kind: 'cycle', dim: false },
    { id: 'spaceRollRate', label: 'Roll speed', value: pct(c.spaceRollRate), kind: 'cycle', dim: c.spaceRoll === 'lock' },
    { id: 'vignette', label: 'Comfort vignette', value: vig, kind: 'cycle', dim: false },
    { id: 'spaceCue', label: 'Vignette closes on', value: cue, kind: 'cycle', dim: !(c.vignette > 0) },
    { id: 'spaceFrame', label: 'Cockpit frame', value: c.spaceFrame ? 'On' : 'Off', kind: 'cycle', dim: false },
    { id: 'spaceHudScale', label: 'Cockpit HUD size', value: pct(c.spaceHudScale), kind: 'cycle', dim: false },
    { id: 'spaceEffects', label: 'Screen effects', value: c.spaceEffects === 'full' ? 'Full' : 'Reduced', kind: 'cycle', dim: false },
    { id: 'moveHand', label: 'Throttle hand', value: c.moveHand === 'left' ? 'Left (right flies the nose)' : 'Right (left flies the nose)', kind: 'cycle', dim: false },
    { id: 'seated', label: 'Seated play', value: c.seated ? 'On' : 'Off', kind: 'cycle', dim: false },
    { id: 'heightOffsetM', label: 'Height nudge', value: cm(c.heightOffsetM), kind: 'step', dim: false },
    { id: 'reset', label: 'Defaults', value: 'Reset all', kind: 'action', dim: false },
  ];
}

/** The tile grid: two columns, six rows, filling the content area. */
export function comfortTileRects(n: number): Rect[] {
  const C = LAYOUT.content;
  const gapX = 16, gapY = 12, cols = 2, rows = 6;
  const w = (C.w - gapX * (cols - 1)) / cols;
  const h = (C.h - gapY * (rows - 1)) / rows;
  const out: Rect[] = [];
  for (let i = 0; i < n; i++) {
    const col = Math.floor(i / rows), row = i % rows;
    out.push({ x: C.x + col * (w + gapX), y: C.y + row * (h + gapY), w, h });
  }
  return out;
}

/** Which third of a step tile a click lands in. */
export function stepZone(r: Rect, px: number): 'left' | 'mid' | 'right' {
  const z = Math.min(96, r.w / 3);
  return px < r.x + z ? 'left' : px >= r.x + r.w - z ? 'right' : 'mid';
}

/**
 * A click on a Comfort tile → the patch for `setComfort`, 'reset' for the defaults, or null (a dead zone). The
 * locomotion agent READS these values; this page never implements the behaviour.
 */
export function comfortClick(c: XRComfort, id: ComfortTileId, zone: 'left' | 'mid' | 'right' = 'mid'): Partial<XRComfort> | 'reset' | null {
  switch (id) {
    case 'move': return { move: c.move === 'slide' ? 'teleport' : 'slide' };
    case 'turn': return { turn: c.turn === 'snap' ? 'smooth' : 'snap' };
    case 'snapDeg': return { snapDeg: nextChoice(SNAP_CHOICES, c.snapDeg) };
    case 'smoothDegPerS': return { smoothDegPerS: nextChoice(SMOOTH_CHOICES, c.smoothDegPerS) };
    case 'vignette': return { vignette: nextChoice(VIGNETTE_CHOICES.map((x) => x.v), c.vignette) };
    case 'seated': return { seated: !c.seated };
    case 'seatedOffsetM': return { seatedOffsetM: nextChoice(SEATED_OFFSET_CHOICES, c.seatedOffsetM) };
    case 'heightOffsetM': {
      if (zone === 'mid') return null;
      const next = Math.round((c.heightOffsetM + (zone === 'left' ? -HEIGHT_STEP_M : HEIGHT_STEP_M)) * 100) / 100;
      return { heightOffsetM: Math.max(-0.5, Math.min(0.5, next)) };
    }
    case 'moveHand': return { moveHand: c.moveHand === 'left' ? 'right' : 'left' };
    case 'vehicleComfort': return { vehicleComfort: !c.vehicleComfort };
    case 'spaceBank': return { spaceBank: c.spaceBank === 'off' ? 'reduced' : c.spaceBank === 'reduced' ? 'full' : 'off' };
    case 'spaceRoll': return { spaceRoll: c.spaceRoll === 'assist' ? 'lock' : c.spaceRoll === 'lock' ? 'free' : 'assist' };
    case 'spaceRollRate': return { spaceRollRate: nextChoice(SPACE_ROLL_RATE_CHOICES, c.spaceRollRate) };
    case 'spaceCue': return { spaceCue: c.spaceCue === 'all' ? 'turns' : c.spaceCue === 'turns' ? 'off' : 'all' };
    case 'spaceFrame': return { spaceFrame: !c.spaceFrame };
    case 'spaceHudScale': return { spaceHudScale: nextChoice(SPACE_HUD_SCALE_CHOICES, c.spaceHudScale) };
    case 'spaceEffects': return { spaceEffects: c.spaceEffects === 'reduced' ? 'full' : 'reduced' };
    case 'reset': return 'reset';
    default: return null;
  }
}

/** The move hand's partner: the wrist menu lives on the MOVE hand, the pointer is the other (turn) hand. */
export function turnHandOf(c: Pick<XRComfort, 'moveHand'>): 'left' | 'right' {
  return c.moveHand === 'left' ? 'right' : 'left';
}

export { COMFORT_DEFAULTS };

// ─── The Perf page (reads lib/xr_perf) ──────────────────────────────────────────

export type Level = 'good' | 'warn' | 'bad' | 'info';
export interface StatTile { label: string; value: string; sub: string; frac: number; level: Level }

/** Meta's WebXR budgets: ~100 draws comfortable, 200 tight, 400 stalls; 150–300 k visible triangles workable. */
export const DRAW_BUDGET = 150;
export const TRI_BUDGET = 300_000;

const k = (n: number): string => (n >= 1e6 ? `${(n / 1e6).toFixed(2)} M` : n >= 1e3 ? `${Math.round(n / 1e3)} k` : `${Math.round(n)}`);

export function perfTiles(s: XRPerfSnapshot): StatTile[] {
  const b = s.budgetMs > 0 ? s.budgetMs : 1000 / 72;
  const lvl = (v: number, good: number, warn: number): Level => (v <= good ? 'good' : v <= warn ? 'warn' : 'bad');
  const noData = s.frames === 0;
  return [
    { label: 'Frame (median)', value: noData ? '—' : `${s.p50Ms.toFixed(1)} ms`, sub: `budget ${b.toFixed(1)} ms`, frac: noData ? 0 : s.p50Ms / b, level: noData ? 'info' : lvl(s.p50Ms, b * 1.05, b * 1.25) },
    { label: 'Frame (95th)', value: noData ? '—' : `${s.p95Ms.toFixed(1)} ms`, sub: `${s.frames} frames`, frac: noData ? 0 : s.p95Ms / b, level: noData ? 'info' : lvl(s.p95Ms, b * 1.5, b * 2) },
    { label: 'Missed frames', value: noData ? '—' : `${(s.missShare * 100).toFixed(1)} %`, sub: '> 1.5 budgets', frac: s.missShare / 0.2, level: noData ? 'info' : lvl(s.missShare, 0.02, 0.1) },
    { label: 'Target', value: `${Math.round(s.targetHz)} Hz`, sub: `${b.toFixed(1)} ms / frame`, frac: 0, level: 'info' },
    { label: 'Draw calls', value: `${Math.round(s.draws)}`, sub: `budget ${DRAW_BUDGET}`, frac: s.draws / (DRAW_BUDGET * 2), level: lvl(s.draws, DRAW_BUDGET, 200) },
    { label: 'Triangles', value: k(s.triangles), sub: `budget ${k(TRI_BUDGET)}`, frac: s.triangles / (TRI_BUDGET * 2), level: lvl(s.triangles, TRI_BUDGET, 500_000) },
    { label: 'Quality level', value: `${s.level}`, sub: '0 = best', frac: 0, level: 'info' },
    { label: 'Foveation', value: s.foveation.toFixed(2), sub: '0 off … 1 max', frac: s.foveation, level: 'info' },
    { label: 'Render scale', value: s.framebufferScale.toFixed(2), sub: 'of the eye buffer', frac: s.framebufferScale, level: 'info' },
    { label: 'Headset', value: s.headset || 'unknown', sub: 'profile', frac: 0, level: 'info' },
  ];
}

export function statTileRects(n: number): Rect[] {
  const C = LAYOUT.content;
  const cols = 3, gap = 14;
  const rows = Math.max(1, Math.ceil(n / cols));
  const w = (C.w - gap * (cols - 1)) / cols;
  const h = Math.min(132, (C.h - gap * (rows - 1)) / rows);
  const out: Rect[] = [];
  for (let i = 0; i < n; i++) out.push({ x: C.x + (i % cols) * (w + gap), y: C.y + Math.floor(i / cols) * (h + gap), w, h });
  return out;
}

// ─── The Map page ───────────────────────────────────────────────────────────────

export const MAP_RADII = [120, 300, 800, 2000] as const;
export const MAP_RECT: Rect = { x: LAYOUT.content.x, y: LAYOUT.content.y, w: 576, h: 576 };
export const MAP_SIDE: Rect = { x: LAYOUT.content.x + 592, y: LAYOUT.content.y, w: 160, h: 576 };

/** North-up projection: world (x, z) → canvas px inside `rect`, centred on (cx, cz), `radius` metres to the edge. */
export function mapProject(cx: number, cz: number, radius: number, rect: Rect): (x: number, z: number) => { px: number; py: number } {
  const s = Math.min(rect.w, rect.h) / 2 / Math.max(1, radius);
  const mx = rect.x + rect.w / 2, my = rect.y + rect.h / 2;
  return (x, z) => ({ px: mx + (x - cx) * s, py: my + (z - cz) * s });
}

// ─── The wrist menu ─────────────────────────────────────────────────────────────

export type WristItemId = 'map' | 'inventory' | 'chat' | 'talk' | 'comfort' | 'perf' | 'panel' | 'close';
export const WRIST_ITEMS: ReadonlyArray<{ id: WristItemId; label: string; icon: string }> = [
  { id: 'map', label: 'Map', icon: 'map' },
  { id: 'inventory', label: 'Items', icon: 'box' },
  { id: 'chat', label: 'Chat', icon: 'chat' },
  { id: 'talk', label: 'Talk', icon: 'talk' },
  { id: 'comfort', label: 'Comfort', icon: 'comfort' },
  { id: 'perf', label: 'Perf', icon: 'chart' },
  { id: 'panel', label: 'Panel', icon: 'menu' },
  { id: 'close', label: 'Close', icon: 'close' },
];
/** Wrist grid: 4 × 2 buttons of 112 px (35 mm) with 38 px (12 mm) between them, under a 120 px combat strip. */
export const WRIST_LAYOUT = { hud: { x: 24, y: 16, w: 592, h: 128 }, button: 112, gap: 38, top: 168 } as const;

export function wristWidgets(): Widget[] {
  const L = WRIST_LAYOUT;
  return WRIST_ITEMS.map((it, i) => ({
    id: `wrist:${it.id}`,
    rect: { x: L.gap + (i % 4) * (L.button + L.gap), y: L.top + Math.floor(i / 4) * (L.button + L.gap), w: L.button, h: L.button },
  }));
}

/** Where a wrist item leads: a page of the world panel, a talk, the panel as it was, or closing the menu. */
export function wristTarget(id: WristItemId): { kind: 'page'; page: PageId } | { kind: 'talk' } | { kind: 'panel' } | { kind: 'close' } {
  switch (id) {
    case 'map': return { kind: 'page', page: 'map' };
    case 'inventory': return { kind: 'page', page: 'inventory' };
    case 'chat': return { kind: 'page', page: 'chat' };
    case 'comfort': return { kind: 'page', page: 'comfort' };
    case 'perf': return { kind: 'page', page: 'perf' };
    case 'talk': return { kind: 'talk' };
    case 'panel': return { kind: 'panel' };
    default: return { kind: 'close' };
  }
}

// ─── Canvas-only icons the shared set lacks (24×24, stroke, the lib/icons convention) ──

export const EXTRA_ICON_PATHS: Readonly<Record<string, string[]>> = {
  chat: ['M4 5.5h16v10H9.5L5.5 19v-3.5H4z', 'M8 9.5h8', 'M8 12.5h5'],
  talk: ['M12 4.2a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8', 'M5.5 20c.6-3.8 3.2-6 6.5-6s5.9 2.2 6.5 6', 'M18.5 3.5c1.2.9 1.9 2.2 1.9 3.6s-.7 2.7-1.9 3.6'],
  comfort: ['M12 3.5a8.5 8.5 0 1 0 8.5 8.5', 'M20.5 3.5v5h-5', 'M8.5 12.5l2.4 2.4 4.6-5'],
};
